import type { FillPattern } from "components/project/shapefiles/shapefiles.constants";
import {
  FILL_PATTERN_OPTIONS,
  getFillPatternImageName
} from "components/project/shapefiles/shapefiles.constants";

const TILE_SIZE = 32;
const LINE_WIDTH = 2;

type PatternDrawer = (ctx: CanvasRenderingContext2D, size: number, color: string) => void;

const drawForwardSlash: PatternDrawer = (ctx, size, color) => {
  ctx.strokeStyle = color;
  ctx.lineWidth = LINE_WIDTH;
  ctx.beginPath();
  // Draw two diagonal lines to ensure seamless tiling
  ctx.moveTo(0, size);
  ctx.lineTo(size, 0);
  ctx.moveTo(-size / 2, size / 2);
  ctx.lineTo(size / 2, -size / 2);
  ctx.moveTo(size / 2, size + size / 2);
  ctx.lineTo(size + size / 2, size / 2);
  ctx.stroke();
};

const drawBackSlash: PatternDrawer = (ctx, size, color) => {
  ctx.strokeStyle = color;
  ctx.lineWidth = LINE_WIDTH;
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(size, size);
  ctx.moveTo(-size / 2, size / 2);
  ctx.lineTo(size / 2, size + size / 2);
  ctx.moveTo(size / 2, -size / 2);
  ctx.lineTo(size + size / 2, size / 2);
  ctx.stroke();
};

const drawVertical: PatternDrawer = (ctx, size, color) => {
  ctx.strokeStyle = color;
  ctx.lineWidth = LINE_WIDTH;
  const spacing = size / 3;
  for (let x = spacing / 2; x < size; x += spacing) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, size);
    ctx.stroke();
  }
};

const drawHorizontal: PatternDrawer = (ctx, size, color) => {
  ctx.strokeStyle = color;
  ctx.lineWidth = LINE_WIDTH;
  const spacing = size / 3;
  for (let y = spacing / 2; y < size; y += spacing) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(size, y);
    ctx.stroke();
  }
};

const drawCross: PatternDrawer = (ctx, size, color) => {
  drawVertical(ctx, size, color);
  drawHorizontal(ctx, size, color);
};

const drawDiagonalCross: PatternDrawer = (ctx, size, color) => {
  drawForwardSlash(ctx, size, color);
  drawBackSlash(ctx, size, color);
};

const drawSmallCircle: PatternDrawer = (ctx, size, color) => {
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  const spacing = size / 2;
  const radius = size / 8;
  for (let x = spacing / 2; x < size; x += spacing) {
    for (let y = spacing / 2; y < size; y += spacing) {
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.stroke();
    }
  }
};

const drawLargeCircle: PatternDrawer = (ctx, size, color) => {
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  const radius = size / 4;
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, radius, 0, Math.PI * 2);
  ctx.stroke();
};

const drawDots: PatternDrawer = (ctx, size, color) => {
  ctx.fillStyle = color;
  const spacing = size / 4;
  const radius = 1.5;
  for (let x = spacing / 2; x < size; x += spacing) {
    for (let y = spacing / 2; y < size; y += spacing) {
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();
    }
  }
};

const drawStars: PatternDrawer = (ctx, size, color) => {
  ctx.fillStyle = color;
  const spacing = size / 2;
  const starSize = size / 6;
  for (let cx = spacing / 2; cx < size; cx += spacing) {
    for (let cy = spacing / 2; cy < size; cy += spacing) {
      drawStar(ctx, cx, cy, starSize);
    }
  }
};

const drawPlus: PatternDrawer = (ctx, size, color) => {
  ctx.strokeStyle = color;
  ctx.lineWidth = LINE_WIDTH;
  const spacing = size / 2;
  const arm = size / 6;
  for (let cx = spacing / 2; cx < size; cx += spacing) {
    for (let cy = spacing / 2; cy < size; cy += spacing) {
      ctx.beginPath();
      ctx.moveTo(cx - arm, cy);
      ctx.lineTo(cx + arm, cy);
      ctx.moveTo(cx, cy - arm);
      ctx.lineTo(cx, cy + arm);
      ctx.stroke();
    }
  }
};

function drawStar(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number) {
  const spikes = 5;
  const outerRadius = r;
  const innerRadius = r / 2;
  ctx.beginPath();
  for (let i = 0; i < spikes * 2; i++) {
    const radius = i % 2 === 0 ? outerRadius : innerRadius;
    const angle = (Math.PI * i) / spikes - Math.PI / 2;
    const x = cx + Math.cos(angle) * radius;
    const y = cy + Math.sin(angle) * radius;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
  ctx.fill();
}

const PATTERN_DRAWERS: Record<Exclude<FillPattern, "none">, PatternDrawer> = {
  "forward-slash": drawForwardSlash,
  "back-slash": drawBackSlash,
  vertical: drawVertical,
  horizontal: drawHorizontal,
  cross: drawCross,
  "diagonal-cross": drawDiagonalCross,
  "small-circle": drawSmallCircle,
  "large-circle": drawLargeCircle,
  dots: drawDots,
  stars: drawStars,
  plus: drawPlus
};

/**
 * Generate a canvas-based fill pattern tile image.
 * Returns an ImageData object that can be passed to `mapbox.addImage()`.
 */
export function generatePatternImage(
  pattern: FillPattern,
  color: string,
  size: number = TILE_SIZE
): ImageData | null {
  if (pattern === "none") return null;

  const drawer = PATTERN_DRAWERS[pattern];
  if (!drawer) return null;

  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  // Transparent background
  ctx.clearRect(0, 0, size, size);
  drawer(ctx, size, color);

  return ctx.getImageData(0, 0, size, size);
}

/**
 * Ensure a fill-pattern image is registered on the map.
 * If the image already exists, it will be updated with the new color.
 */
export function ensureFillPatternImage(
  mapbox: mapboxgl.Map,
  pattern: FillPattern,
  color: string
): string | null {
  if (pattern === "none") return null;

  const imageName = getFillPatternImageName(pattern, color);
  const imageData = generatePatternImage(pattern, color);
  if (!imageData) return null;

  if (mapbox.hasImage(imageName)) {
    mapbox.updateImage(imageName, imageData);
  } else {
    mapbox.addImage(imageName, imageData, { pixelRatio: 2 });
  }

  return imageName;
}

/**
 * Generate a pattern preview as a data URL for use in UI components.
 */
export function generatePatternPreview(
  pattern: FillPattern,
  color: string,
  size: number = 40
): string {
  if (pattern === "none") return "";

  const drawer = PATTERN_DRAWERS[pattern];
  if (!drawer) return "";

  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) return "";

  ctx.clearRect(0, 0, size, size);
  drawer(ctx, size, color);

  return canvas.toDataURL();
}

/**
 * Get the list of fill pattern options (excluding "none") for UI display.
 */
export const FILL_PATTERN_DISPLAY_OPTIONS = FILL_PATTERN_OPTIONS.filter(
  (opt) => opt.value !== "none"
);
